import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authService } from '../services/auth.service';

const load   = (key, fallback = null) => { try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; } };
const save   = (key, val) => localStorage.setItem(key, JSON.stringify(val));
const remove = (key)      => localStorage.removeItem(key);

// ── Thunks ────────────────────────────────────────────────────────────────────
export const loginThunk = createAsyncThunk('auth/login', async (creds, { rejectWithValue }) => {
  try { const data = await authService.login(creds); return data.data; }
  catch (err) { return rejectWithValue(err.response?.data?.message || 'Login failed'); }
});

export const registerThunk = createAsyncThunk('auth/register', async (payload, { rejectWithValue }) => {
  try { const data = await authService.register(payload); return data.data; }
  catch (err) { return rejectWithValue(err.response?.data?.message || 'Registration failed'); }
});

export const logoutThunk = createAsyncThunk('auth/logout', async (_, { dispatch }) => {
  try { await authService.logout(); } catch {}
  dispatch(logout());
});

export const getMeThunk = createAsyncThunk('auth/getMe', async (_, { rejectWithValue }) => {
  try { const data = await authService.getMe(); return data.data; }
  catch (err) { return rejectWithValue(err.response?.data?.message || 'Failed to load user'); }
});

// ── Slice ─────────────────────────────────────────────────────────────────────
const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user:         load('ss_user'),
    accessToken:  load('ss_access'),
    refreshToken: load('ss_refresh'),
    isLoading:    false,
    error:        null,
  },
  reducers: {
    setTokens(state, { payload }) {
      state.accessToken  = payload.accessToken;
      state.refreshToken = payload.refreshToken;
      save('ss_access',  payload.accessToken);
      save('ss_refresh', payload.refreshToken);
    },
    // ── NEW: update user object in store + localStorage ───────────────────────
    setUser(state, { payload }) {
      state.user = payload;
      save('ss_user', payload);
    },
    logout(state) {
      state.user         = null;
      state.accessToken  = null;
      state.refreshToken = null;
      remove('ss_user');
      remove('ss_access');
      remove('ss_refresh');
    },
    clearError(state) { state.error = null; },
  },
  extraReducers: (builder) => {
    const pending  = (state)              => { state.isLoading = true;  state.error = null; };
    const rejected = (state, { payload }) => { state.isLoading = false; state.error = payload; };

    builder
      .addCase(loginThunk.pending,   pending)
      .addCase(loginThunk.rejected,  rejected)
      .addCase(loginThunk.fulfilled, (state, { payload }) => {
        state.isLoading    = false;
        state.user         = payload.user;
        state.accessToken  = payload.accessToken;
        state.refreshToken = payload.refreshToken;
        save('ss_user',    payload.user);
        save('ss_access',  payload.accessToken);
        save('ss_refresh', payload.refreshToken);
      })
      .addCase(registerThunk.pending,   pending)
      .addCase(registerThunk.rejected,  rejected)
      .addCase(registerThunk.fulfilled, (state, { payload }) => {
        state.isLoading    = false;
        state.user         = payload.user;
        state.accessToken  = payload.accessToken;
        state.refreshToken = payload.refreshToken;
        save('ss_user',    payload.user);
        save('ss_access',  payload.accessToken);
        save('ss_refresh', payload.refreshToken);
      })
      .addCase(getMeThunk.fulfilled, (state, { payload }) => {
        state.user = payload;
        save('ss_user', payload);
      })
      .addCase(logoutThunk.fulfilled, () => {});
  },
});

export const { setTokens, setUser, logout, clearError } = authSlice.actions;

// ── Selectors ─────────────────────────────────────────────────────────────────
export const selectUser        = (s) => s.auth.user;
export const selectIsLoggedIn  = (s) => !!s.auth.accessToken;
export const selectPlan        = (s) => s.auth.user?.plan || 'starter';
export const selectAuthLoading = (s) => s.auth.isLoading;
export const selectAuthError   = (s) => s.auth.error;
export const isPro             = (s) => ['pro'].includes(s.auth.user?.plan);

export default authSlice.reducer;